import axios from "axios";
import React, { useState, useEffect } from "react";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col } from "react-bootstrap";
import { getFirstAndLastEmiDates,
  resolveEffectiveStartDate,
} from "../../../utils/utils";
import Select from "react-select";
import CustomPagination from "../../../components/ui/CustomPagination";
import DataTable from "../../../components/ui/DataTable";
import { useAdminAuth } from "../../auth/AdminAuthProvider";
import DashboardHeader from "../../../components/layout/DashboardHeader";
import { MdExpandMore } from "react-icons/md";
import StandardizedDateInput from "../../../components/ui/StandardizedDateInput";

const SecurityDepositReport = () => {
  const { adminUser } = useAdminAuth();
  const [reportData, setReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null); // State for branch name filter
  const [selectedEmployeeFilter, setSelectedEmployeeFilter] = useState(null); // State for employee name filter
  const [searchTerm, setSearchTerm] = useState("");

  const itemsPerPage = 20;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const paginationData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  useEffect(() => {
    const getReportData = async (id) => {
      try {
        const response = await axios.get(
          `${BASE_URL}/api/getFutureDemandReportData`,
          { params: { id } }
        );
        // console.log(
        //   "clientProspect response.data: " + JSON.stringify(response.data)
        // );
        if (response.data) {
          setReportData(response.data);
        } else {
          console.log("Error in fetching Data");
        }
      } catch (error) {
        console.log("Error fetching Data:", error);
      }
    };

    getReportData(adminUser.user.id);
  }, [adminUser.user.id]);

  // Extract unique branch names and employee names for dropdown options
  const branchOptions = [
    ...new Set(filteredData.map((data) => data?.branchName)),
  ]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  const employeeOptions = [
    ...new Set(filteredData.map((data) => data?.username)),
  ]
    .filter((name) => name)
    .map((name) => ({ value: name, label: name }));

  // Automatically filter data when fromDate, toDate, or searchTerm changes
  useEffect(() => {
    let filtered = reportData;

    if (fromDate && toDate) {
      filtered = filtered.filter((item) => {
        const updatedAt = new Date(item.branchManagerStatusUpdatedAt);
        const start = new Date(fromDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        return updatedAt >= start && updatedAt <= end;
      });
    }

    if (selectedBranchFilter) {
      filtered = filtered.filter(
        (item) => item.branchName === selectedBranchFilter.value
      );
    }

    if (selectedEmployeeFilter) {
      filtered = filtered.filter(
        (item) => item.username === selectedEmployeeFilter.value
      );
    }

    if (searchTerm && searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        const memberName = String(item?.memberName || "").toLowerCase();
        const customerId = String(item?.customerId || "").toLowerCase();
        const loanId = String(item?.loanId || "").toLowerCase();
        const phoneNumber = String(item?.phoneNumber || "").toLowerCase();
        const aadharNo = String(item?.aadharNo || "").toLowerCase();
        const applicationId = String(item?.ApplicationId || "").toLowerCase();

        return (
          memberName.includes(term) ||
          customerId.includes(term) ||
          loanId.includes(term) ||
          phoneNumber.includes(term) ||
          aadharNo.includes(term) ||
          applicationId.includes(term)
        );
      });
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [
    fromDate,
    toDate,
    reportData,
    selectedBranchFilter,
    selectedEmployeeFilter,
    searchTerm,
  ]);

  // Define columns with label and render logic
  const columns = [
    { label: "S No", render: (data, index) => index + 1 },
    { label: "Region", render: (data) => data.regionName },
    { label: "Division", render: (data) => data.divisionName },
    { label: "Branch Name", render: (data) => data.branchName },
    { label: "Branch Code", render: (data) => data.branchCode },
    { label: "Customer Name", render: (data) => data.memberName },
    { label: "Customer ID", render: (data) => data.customerId },
    {
      label: "Address",
      render: (data) =>
        `${data.permanentDoorNo},${data.permanentStreetNo},${data.permanentPanchayat},${data.permanentTaluk},${data.permanentDistrict}-${data.permanentPincode}`,
    },
    { label: "Contact No", render: (data) => data.phoneNumber },
    { label: "Loan Type", render: (data) => data.loanType },
    {
      label: "Sub Category",
      render: (data) => data.businessDetails.natureOfBusiness,
    },
    { label: "Loan Cycle", render: (data) => data.loanCycle },
    { label: "Loan Account No", render: (data) => data.loanId },
    {
      label: "Rate of Interest",
      render: (data) => data.proposedLoanDetails.rateOfInterest,
    },
    {
      label: "Disbursement Date",
      render: (data) =>
        data.branchManagerStatus === "disbursed" &&
          data.branchManagerStatusUpdatedAt
          ? new Date(data.branchManagerStatusUpdatedAt).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          })
          : "",
    },
    {
      label: "Maturity Date",
      render: (data) => {
        const { lastEmiDate } = getFirstAndLastEmiDates(
          data.branchManagerStatusUpdatedAt,
          data.proposedLoanDetails.tenureInMonths,
          data.emiDateByBranchManager,
          resolveEffectiveStartDate(data)
        );
        return lastEmiDate ? lastEmiDate.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }) : "";
      },
    },

    {
      label: "Loan Amount",
      render: (data) =>
        data.branchManagerStatus === "disbursed"
          ? data.sanctionedLoanAmountBySanctionCommittee
          : "",
    },
    { label: "Employee Name", render: (data) => data.employeeName },
    { label: "Prospect ID", render: (data) => data.ApplicationId },
    { label: "Security Deposit", render: (data) => data.securityDeposit },
    // Add more columns as needed with custom render logic
  ];

  return (
    <section className="master-layout">
      <div className="master-sidebar-wrapper">
        <Sidebar
          openSidebarToggle={openSidebarToggle}
          OpenSidebar={OpenSidebar}
        />
      </div>

      <div className="master-content-wrapper">
        <div className="master-header-wrapper">
          <DashboardHeader OpenSidebar={OpenSidebar} />
        </div>

        <div className="master-card-container">
          <div className="master-search-section">
            <h1 className="TableHeaderText w-100 text-start">Security Deposit Report</h1>

            <button
              className={`filter-toggle-btn ${showMobileFilters ? "active" : ""}`}
              onClick={() => setShowMobileFilters(!showMobileFilters)}
            >
              <span style={{ fontWeight: 600, fontSize: "14px" }}>
                {showMobileFilters ? "Hide Filters" : "Show Filters"}
              </span>
              <MdExpandMore size={22} />
            </button>

            <div className={`filter-container ${!showMobileFilters ? "mobile-hidden" : "mobile-visible"}`}>
              <Row>
                <Col xs={12} lg={4} md={6} className="mb-2">
                  <Form.Group>
                    <Form.Label>Search Customer</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search Name, Customer ID, Loan Acc No, Phone..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>From Date</Form.Label>
                    <StandardizedDateInput
                      value={fromDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setFromDate(val);
                        if (val && toDate && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={2} md={6}>
                  <Form.Group>
                    <Form.Label>To Date</Form.Label>
                    <StandardizedDateInput
                      value={toDate}
                      onChange={(event) => {
                        const val = event.target.value;
                        setToDate(val);
                        if (fromDate && val && selectedBranchFilter && selectedEmployeeFilter) {
                          setTimeout(() => setShowMobileFilters(false), 300);
                        }
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12} lg={4} md={6}>
                  <Form.Label>Branch</Form.Label>
                  <Select
                    value={selectedBranchFilter}
                    onChange={(val) => {
                      setSelectedBranchFilter(val);
                      if (fromDate && toDate && val && selectedEmployeeFilter) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={branchOptions}
                    isClearable
                    placeholder="Filter by Branch Name"
                    className="filter-dropdown"
                  />
                </Col>
                <Col xs={12} lg={4} md={6}>
                  <Form.Label>Employee</Form.Label>
                  <Select
                    value={selectedEmployeeFilter}
                    onChange={(val) => {
                      setSelectedEmployeeFilter(val);
                      if (fromDate && toDate && selectedBranchFilter && val) {
                        setTimeout(() => setShowMobileFilters(false), 300);
                      }
                    }}
                    options={employeeOptions}
                    isClearable
                    placeholder="Filter by Employee Name"
                    className="filter-dropdown"
                  />
                </Col>
              </Row>
            </div>
          </div>

          <div className="master-list-section">
            <div className="master-table-wrapper" style={{ padding: "0 15px" }}>
              <DataTable
                columns={columns}
                data={paginationData}
                reportName="SecurityDepositReport"
                excelData={filteredData}
              />
            </div>

            <div className="master-pagination-wrapper">
              <CustomPagination
                totalPages={totalPages}
                currentPage={currentPage}
                paginate={paginate}
                totalItems={filteredData.length}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecurityDepositReport;
